import React, { useState, useMemo } from 'react';
import './EventExplorer.css';

interface HistoryEvent {
  id: string;
  year: number;
  category: string;
  description: string;
  significance: number;
  tags: string[];
  actors?: string[];
  location?: string;
}

interface HistoryEntity {
  id: string;
  type: string;
  name: string;
  createdYear: number;
  currentStatus: string;
  description?: string;
}

interface EventExplorerProps {
  events: HistoryEvent[];
  entities: HistoryEntity[];
  onEventClick: (event: HistoryEvent) => void;
  onEntityClick: (entity: HistoryEntity) => void;
}

export const EventExplorer: React.FC<EventExplorerProps> = ({
  events,
  entities,
  onEventClick,
  onEntityClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSignificance, setSelectedSignificance] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'year' | 'significance' | 'category'>('year');
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'timeline'>('list');

  const categories = useMemo(() => {
    return Array.from(new Set(events.map(e => e.category))).sort();
  }, [events]);

  const filteredEvents = useMemo(() => {
    return events
      .filter(event => {
        const matchesSearch = 
          event.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        
        const matchesCategory = selectedCategory === 'all' || event.category === selectedCategory;
        
        const matchesSignificance = 
          selectedSignificance === 'all' ||
          (selectedSignificance === 'high' && event.significance >= 7) ||
          (selectedSignificance === 'medium' && event.significance >= 4 && event.significance < 7) ||
          (selectedSignificance === 'low' && event.significance < 4);

        return matchesSearch && matchesCategory && matchesSignificance;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'year':
            return a.year - b.year;
          case 'significance':
            return b.significance - a.significance;
          case 'category':
            return a.category.localeCompare(b.category);
          default:
            return 0;
        }
      });
  }, [events, searchQuery, selectedCategory, selectedSignificance, sortBy]);

  const getEventColor = (event: HistoryEvent): string => {
    if (event.significance >= 9) return '#e74c3c';
    if (event.significance >= 7) return '#f39c12';
    if (event.significance >= 5) return '#3498db';
    return '#95a5a6';
  };

  const getRelatedEntities = (event: HistoryEvent): HistoryEntity[] => {
    return entities.filter(entity =>
      event.actors?.includes(entity.id) ||
      event.description.includes(entity.name)
    );
  };

  const getEventsByYear = (year: number): HistoryEvent[] => {
    return filteredEvents.filter(e => e.year === year);
  };

  const yearsWithEvents = useMemo(() => {
    return Array.from(new Set(filteredEvents.map(e => e.year))).sort((a, b) => a - b);
  }, [filteredEvents]);

  return (
    <div className="event-explorer">
      <div className="explorer-header">
        <h3>Event Explorer</h3>
        <p className="explorer-subtitle">
          {filteredEvents.length} events found • {yearsWithEvents.length} years represented
        </p>
      </div>

      <div className="explorer-controls">
        <div className="control-group">
          <input
            type="text"
            placeholder="Search events, tags, descriptions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="control-group">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={selectedSignificance}
            onChange={(e) => setSelectedSignificance(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Significance</option>
            <option value="high">High (7-10)</option>
            <option value="medium">Medium (4-6)</option>
            <option value="low">Low (1-3)</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'year' | 'significance' | 'category')}
            className="filter-select"
          >
            <option value="year">Sort by Year</option>
            <option value="significance">Sort by Significance</option>
            <option value="category">Sort by Category</option>
          </select>
        </div>

        <div className="view-modes">
          <button
            className={`view-mode ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List View"
          >
            📋
          </button>
          <button
            className={`view-mode ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid View"
          >
            ⊞
          </button>
          <button
            className={`view-mode ${viewMode === 'timeline' ? 'active' : ''}`}
            onClick={() => setViewMode('timeline')}
            title="Timeline View"
          >
            📊
          </button>
        </div>
      </div>

      <div className="explorer-content">
        {viewMode === 'list' && (
          <div className="events-list">
            {filteredEvents.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h4>No events found</h4>
                <p>Try adjusting your filters or search terms</p>
              </div>
            ) : (
              filteredEvents.map((event, index) => (
                <div
                  key={event.id}
                  className={`event-card ${index === 0 ? 'featured' : ''}`}
                  onClick={() => onEventClick(event)}
                >
                  <div className="event-card-header">
                    <div className="event-year-badge">Year {event.year}</div>
                    <div className="event-category-badge">{event.category}</div>
                    <div 
                      className="event-significance-badge"
                      style={{ background: getEventColor(event) }}
                    >
                      ★ {event.significance}
                    </div>
                  </div>
                  
                  <p className="event-card-description">{event.description}</p>
                  
                  <div className="event-card-footer">
                    <div className="event-tags">
                      {event.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                      {event.tags.length > 3 && (
                        <span className="tag">+{event.tags.length - 3}</span>
                      )}
                    </div>
                    
                    {event.location && (
                      <div className="event-location">📍 {event.location}</div>
                    )}
                  </div>

                  {getRelatedEntities(event).length > 0 && (
                    <div className="event-entities">
                      {getRelatedEntities(event).slice(0, 3).map(entity => (
                        <span
                          key={entity.id}
                          className="entity-tag"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEntityClick(entity);
                          }}
                        >
                          {entity.type === 'faction' ? '⚔️' : '👤'} {entity.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {viewMode === 'grid' && (
          <div className="events-grid">
            {filteredEvents.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h4>No events found</h4>
                <p>Try adjusting your filters or search terms</p>
              </div>
            ) : (
              filteredEvents.map(event => (
                <div
                  key={event.id}
                  className="event-grid-card"
                  onClick={() => onEventClick(event)}
                  style={{ borderLeft: `4px solid ${getEventColor(event)}` }}
                >
                  <div className="grid-card-year">Year {event.year}</div>
                  <div className="grid-card-category">{event.category}</div>
                  <p className="grid-card-description">{event.description}</p>
                  <div className="grid-card-significance">★ {event.significance}/10</div>
                </div>
              ))
            )}
          </div>
        )}

        {viewMode === 'timeline' && (
          <div className="events-timeline">
            {yearsWithEvents.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📅</div>
                <h4>No events to display</h4>
                <p>Try adjusting your filters</p>
              </div>
            ) : (
              yearsWithEvents.map(year => {
                const yearEvents = getEventsByYear(year);
                return (
                  <div key={year} className="timeline-year">
                    <div className="timeline-year-marker">
                      <div className="year-number">{year}</div>
                      <div className="year-count">{yearEvents.length} events</div>
                    </div>
                    <div className="timeline-events">
                      {yearEvents.map(event => (
                        <div
                          key={event.id}
                          className="timeline-event"
                          onClick={() => onEventClick(event)}
                          style={{ borderLeft: `3px solid ${getEventColor(event)}` }}
                        >
                          <div className="timeline-event-category">{event.category}</div>
                          <p className="timeline-event-description">{event.description}</p>
                          <div className="timeline-event-meta">
                            <span className="significance">★ {event.significance}</span>
                            {event.location && <span className="location">📍 {event.location}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};
